import { Currency, filterCurrencies, getCurrencyByCode, SUPPORTED_CURRENCIES } from '@/constants/currencies'
import { usePortalDropdownPosition } from '@hooks'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
	CurrencyCode,
	CurrencyDropdown,
	CurrencyDropdownItem,
	CurrencyInput,
	CurrencyLabel,
	CurrencyName,
	CurrencySelectContainer,
	CurrencySelectWrapper,
	EmptyCurrencyDropdownItem,
	SearchIcon,
} from './CurrencySelect.styles'

interface CurrencySelectProps {
	value: string
	onChange: (currencyCode: string) => void
	label?: string
	htmlFor?: string
}

const CurrencySelect: React.FC<CurrencySelectProps> = ({ value, onChange, label, htmlFor }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const dropdownCoords = usePortalDropdownPosition(containerRef, isOpen)

	const selectedCurrency = getCurrencyByCode(value) || SUPPORTED_CURRENCIES.find(c => c.code === 'usd')!

	const filteredCurrencies = filterCurrencies(searchQuery)

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const insideContainer = containerRef.current?.contains(event.target as Node)
			const insideDropdown = dropdownRef.current?.contains(event.target as Node)
			if (!insideContainer && !insideDropdown) {
				setIsOpen(false)
				setSearchQuery('')
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleSelect = (currency: Currency) => {
		onChange(currency.code)
		setIsOpen(false)
		setSearchQuery('')
	}

	const handleInputFocus = () => {
		setIsOpen(true)
	}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value)
		setIsOpen(true)
	}

	const displayValue = isOpen ? searchQuery : `${selectedCurrency.symbol} ${selectedCurrency.code.toUpperCase()} - ${selectedCurrency.name}`

	return (
		<CurrencySelectContainer>
			{label && (
				<CurrencyLabel htmlFor={htmlFor}>
					{label}
				</CurrencyLabel>
			)}
			<CurrencySelectWrapper ref={containerRef}>
				<CurrencyInput
					ref={inputRef}
					id={htmlFor}
					aria-label={label}
					type="text"
					value={displayValue}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					placeholder="Search currency..."
					readOnly={!isOpen}
				/>
				<SearchIcon>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
							stroke="currentColor"
							strokeWidth="1.333"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</SearchIcon>
				{isOpen &&
					dropdownCoords &&
					createPortal(
						<CurrencyDropdown
							ref={dropdownRef}
							$top={dropdownCoords.top}
							$left={dropdownCoords.left}
							$width={dropdownCoords.width}
						>
							{filteredCurrencies.length > 0 ? (
								filteredCurrencies.map(currency => (
									<CurrencyDropdownItem
										key={currency.code}
										onClick={() => handleSelect(currency)}
										$isSelected={currency.code === value}
									>
										<CurrencyCode>
											{currency.symbol} {currency.code.toUpperCase()}
										</CurrencyCode>
										<CurrencyName>{currency.name}</CurrencyName>
									</CurrencyDropdownItem>
								))
							) : (
								<EmptyCurrencyDropdownItem $isSelected={false}>
									No currencies found
								</EmptyCurrencyDropdownItem>
							)}
						</CurrencyDropdown>,
						document.body,
					)}
			</CurrencySelectWrapper>
		</CurrencySelectContainer>
	)
}

export default CurrencySelect

